import { createFileRoute } from "@tanstack/react-router";
import { Sparkles } from "lucide-react";
import { SiteLayout } from "@/components/SiteLayout";
import { PageHero } from "@/components/PageHero";
import { FinalCTA } from "@/components/home/HomeSections";
import { SCHEMA_ORG_ID, SITE_URL } from "@/lib/entitySchema";

const CANONICAL = `${SITE_URL}/changelog`;

type Entry = { date: string; iso: string; tag: string; items: string[] };

const ENTRIES: Entry[] = [
  {
    date: "April 2026",
    iso: "2026-04-01",
    tag: "Site update",
    items: [
      "Added /case-studies/$slug individual pages with Article schema and full @id graph linkage to the Organization and Person entities.",
      "Launched /team, /how-we-work, /press, /guarantees, /faq, /community, and /learn/glossary — closing the E-E-A-T trust-page gap site-wide.",
      "Wired sticky 'Get a Free Strategy Call' CTA into city and industry pages with local-context button text.",
    ],
  },
  {
    date: "March 2026",
    iso: "2026-03-01",
    tag: "Schema & GEO",
    items: [
      "Implemented sitewide Organization, LocalBusiness, and Person JSON-LD with consistent @id values across every page.",
      "Added WebSite schema with publisher linkage for AI search engines (ChatGPT, Perplexity, Gemini).",
      "Validated entity graph integrity — all city, industry, and service pages now reference the same canonical Organization node.",
    ],
  },
  {
    date: "February 2026",
    iso: "2026-02-01",
    tag: "Local pages",
    items: [
      "Built per-city testimonial blocks for all 13 Colorado service-area pages (Colorado Springs, Denver, Pueblo, Castle Rock, Monument, Fort Collins, Boulder, Parker, Aurora, Lakewood, Thornton, Arvada, Westminster, Centennial).",
      "Added unique highlight metrics and neighborhood-specific quotes per city — no recycled stock testimonials.",
    ],
  },
  {
    date: "January 2026",
    iso: "2026-01-01",
    tag: "Performance",
    items: [
      "Migrated to TanStack Start v1 with full SSR for every route — Core Web Vitals now in the green across the site.",
      "Added Elfsight live Google Reviews widget on /reviews, home, and city/industry pages.",
      "Added Google Tag (gtag.js) for sitewide analytics with proper preconnect hints.",
    ],
  },
];

const PAGE_LD = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      "@id": `${CANONICAL}#page`,
      url: CANONICAL,
      name: "Site & Service Changelog — Colorado Web Impressions",
      description: "Monthly log of what we shipped, learned, and improved.",
      isPartOf: { "@id": `${SITE_URL}/#website` },
      publisher: { "@id": SCHEMA_ORG_ID },
      dateModified: ENTRIES[0]?.iso,
    },
    {
      "@type": "BreadcrumbList",
      "@id": `${CANONICAL}#breadcrumb`,
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
        { "@type": "ListItem", position: 2, name: "Changelog", item: CANONICAL },
      ],
    },
  ],
};

export const Route = createFileRoute("/changelog")({
  head: () => ({
    meta: [
      { title: "Changelog — What We Shipped & Learned | Colorado Web Impressions" },
      { name: "description", content: "Monthly log of what Colorado Web Impressions shipped, learned, and improved — site updates, schema work, performance wins." },
      { property: "og:title", content: "Changelog — Colorado Web Impressions" },
      { property: "og:description", content: "What we shipped, learned, and improved this month." },
      { property: "og:url", content: CANONICAL },
    
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Changelog — Colorado Web Impressions" },
      { name: "twitter:description", content: "Monthly log of what Colorado Web Impressions shipped, learned, and improved — site updates, schema work, performance wins." },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
    scripts: [{ type: "application/ld+json", children: JSON.stringify(PAGE_LD) }],
  }),
  component: ChangelogPage,
});

function ChangelogPage() {
  return (
    <SiteLayout>
      <PageHero
        eyebrow="Changelog"
        title={<>What we <span className="text-brand-gold">shipped, learned, and improved.</span></>}
        description="A running, dated log of what we've changed across the site, our services, and our methodology. Updated monthly."
      />

      <section className="bg-background py-16 lg:py-20">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <ol className="space-y-10 border-l-2 border-brand-gold/30 pl-6">
            {ENTRIES.map((e) => (
              <li key={e.iso} className="relative">
                <span className="absolute -left-[34px] top-1 grid h-6 w-6 place-items-center rounded-full bg-gradient-gold text-brand-black shadow-gold">
                  <Sparkles className="h-3 w-3" />
                </span>
                <div className="flex flex-wrap items-center gap-2">
                  <time dateTime={e.iso} className="text-sm font-bold text-foreground">{e.date}</time>
                  <span className="rounded-full bg-brand-gold/10 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider text-brand-gold-deep">
                    {e.tag}
                  </span>
                </div>
                <ul className="mt-3 space-y-2 rounded-2xl border border-border bg-card p-5 shadow-card">
                  {e.items.map((item) => (
                    <li key={item} className="text-sm leading-relaxed text-foreground/85">• {item}</li>
                  ))}
                </ul>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <FinalCTA />
    </SiteLayout>
  );
}
